import React, { useEffect, useMemo, useState } from 'react';
import newsService from '../../services/newsService';
import newsTaskService from '../../services/newsTaskService';
import newsTaskRecordService from '../../services/newsTaskRecordService';
import api from '../../services/apiClient';
import './dataEditor.css';

// Self-contained JsonEditor implementation and styles
const isObject = (v) => v && typeof v === 'object' && !Array.isArray(v);
const isArray = (v) => Array.isArray(v);
const extendedTypeOf = (v, hint) => {
  if (hint) return hint;
  if (isArray(v)) return 'array';
  if (isObject(v)) return 'object';
  if (typeof v === 'number' && isFinite(v)) return 'number';
  const s = v == null ? '' : String(v);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return 'date';
  if (s.includes('\n') || s.length > 120) return 'textArea';
  return 'string';
};
const todayStr = () => { const d = new Date(); const mm = String(d.getMonth() + 1).padStart(2, '0'); const dd = String(d.getDate()).padStart(2, '0'); return `${d.getFullYear()}-${mm}-${dd}`; };
const defaultForType = (t) => { switch (t) { case 'array': return []; case 'object': return {}; case 'number': return 0; case 'date': return todayStr(); case 'textArea': return ''; case 'string': default: return ''; } };
const convertValueToType = (val, t) => {
  switch (t) {
    case 'array': return Array.isArray(val) ? val : [];
    case 'object': return isObject(val) ? val : {};
    case 'number': { if (typeof val === 'number' && isFinite(val)) return val; const n = parseFloat(String(val ?? '').trim()); return Number.isFinite(n) ? n : 0; }
    case 'date': { const s = String(val ?? '').trim(); if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s; const d = new Date(s); if (!isNaN(d.getTime())) { const mm = String(d.getMonth() + 1).padStart(2, '0'); const dd = String(d.getDate()).padStart(2, '0'); return `${d.getFullYear()}-${mm}-${dd}`; } return todayStr(); }
    case 'textArea':
    case 'string':
    default: return String(val ?? '');
  }
};
const deepClone = (v) => { try { return JSON.parse(JSON.stringify(v)); } catch (_) { return defaultForType(extendedTypeOf(v)); } };
const TypeSelector = ({ value, onChange, typeHint, disabled }) => {
  const t = extendedTypeOf(value, typeHint);
  return (
    <select className="form-select form-select-sm" value={t} onChange={(e) => onChange(convertValueToType(value, e.target.value))} disabled={!!typeHint || disabled}>
      <option value="string">string</option>
      <option value="textArea">textArea</option>
      <option value="number">number</option>
      <option value="date">date</option>
      <option value="array">array</option>
      <option value="object">object</option>
    </select>
  );
};
const StringEditor = ({ value, onChange }) => (
  <input type="text" className="form-control form-control-sm" value={value == null ? '' : String(value)} onChange={(e) => onChange(e.target.value)} />
);
const TextAreaEditor = ({ value, onChange }) => (
  <textarea className="form-control form-control-sm" rows={4} value={value == null ? '' : String(value)} onChange={(e) => onChange(e.target.value)} />
);
const NumberEditor = ({ value, onChange }) => (
  <input type="number" step="any" className="form-control form-control-sm" value={typeof value === 'number' && isFinite(value) ? value : 0} onChange={(e) => { const n = parseFloat(e.target.value); onChange(Number.isFinite(n) ? n : 0); }} />
);
const DateEditor = ({ value, onChange }) => { const toDateVal = (v) => { const s = String(v ?? '').trim(); return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : todayStr(); }; return (<input type="date" className="form-control form-control-sm" value={toDateVal(value)} onChange={(e) => onChange(e.target.value)} />); };
const ObjectEditor = ({ value, onChange, level = 0, fieldTypeHints, lockSchema }) => {
  const entries = Object.entries(value || {});
  const renameKey = (oldKey, newKey) => { if (lockSchema && level === 0) return; if (!newKey || newKey === oldKey) return; const exists = Object.prototype.hasOwnProperty.call(value, newKey); const safeKey = exists ? `${newKey}_1` : newKey; const next = { ...value }; next[safeKey] = next[oldKey]; delete next[oldKey]; onChange(next); };
  const updateChild = (key, nextVal) => { onChange({ ...value, [key]: nextVal }); };
  const removeKey = (key) => { if (lockSchema && level === 0) return; const next = { ...value }; delete next[key]; onChange(next); };
  const addField = () => { if (lockSchema && level === 0) return; let i = entries.length + 1; let key = `key${i}`; while (Object.prototype.hasOwnProperty.call(value, key)) { i += 1; key = `key${i}`; } onChange({ ...value, [key]: '' }); };
  return (
    <div className="json-object editor-block border rounded p-2 bg-light">
      {entries.length === 0 && <div className="text-muted small">(empty object)</div>}
      {entries.map(([k, v], idx) => (
        <div key={`${k}-${idx}`} className="json-field-row d-flex align-items-start gap-2 mb-2">
          <input className="form-control form-control-sm json-key-input" value={k} onChange={(e) => renameKey(k, e.target.value)} readOnly={lockSchema && level === 0} />
          <div className="json-type"><TypeSelector value={v} onChange={(nv) => updateChild(k, nv)} typeHint={fieldTypeHints ? fieldTypeHints[k] : undefined} /></div>
          <div className="json-value flex-grow-1"><ValueEditor value={v} onChange={(nv) => updateChild(k, nv)} level={level + 1} fieldTypeHints={undefined} lockSchema={lockSchema} typeHint={fieldTypeHints ? fieldTypeHints[k] : undefined} /></div>
          {!(lockSchema && level === 0) && (<button type="button" className="btn btn-sm btn-outline-danger json-delete-btn" onClick={() => removeKey(k)}>Delete</button>)}
        </div>
      ))}
      {!(lockSchema && level === 0) && (<button type="button" className="btn btn-sm btn-outline-primary json-add-btn" onClick={addField}>+ Add field</button>)}
    </div>
  );
};
const ArrayEditor = ({ value, onChange, level = 0, fieldTypeHints, lockSchema }) => {
  const items = Array.isArray(value) ? value : [];
  const updateItem = (index, nextVal) => { const next = items.slice(); next[index] = nextVal; onChange(next); };
  const removeItem = (index) => { const next = items.slice(); next.splice(index, 1); onChange(next); };
  const addItem = () => { onChange([...(items || []), '']); };
  return (
    <div className="json-array editor-block border rounded p-2 bg-light">
      {items.length === 0 && <div className="text-muted small">(empty array)</div>}
      {items.map((it, idx) => (
        <div key={idx} className="json-item-row d-flex align-items-start gap-2 mb-2">
          <div className="json-type"><TypeSelector value={it} onChange={(nv) => updateItem(idx, nv)} /></div>
          <div className="json-value flex-grow-1"><ValueEditor value={it} onChange={(nv) => updateItem(idx, nv)} level={level + 1} fieldTypeHints={undefined} lockSchema={lockSchema} /></div>
          <button type="button" className="btn btn-sm btn-outline-danger json-delete-btn" onClick={() => removeItem(idx)}>Delete</button>
        </div>
      ))}
      <button type="button" className="btn btn-sm btn-outline-primary json-add-btn" onClick={addItem}>+ Add item</button>
    </div>
  );
};
const ValueEditor = ({ value, onChange, level = 0, fieldTypeHints, lockSchema, typeHint }) => {
  const t = extendedTypeOf(value, typeHint);
  if (t === 'object') return <ObjectEditor value={value} onChange={onChange} level={level} fieldTypeHints={fieldTypeHints} lockSchema={lockSchema} />;
  if (t === 'array') return <ArrayEditor value={value} onChange={onChange} level={level} fieldTypeHints={fieldTypeHints} lockSchema={lockSchema} />;
  if (t === 'number') return <NumberEditor value={value} onChange={onChange} />;
  if (t === 'date') return <DateEditor value={value} onChange={onChange} />;
  if (t === 'textArea') return <TextAreaEditor value={value} onChange={onChange} />;
  return <StringEditor value={value} onChange={onChange} />;
};
// Content array editor for News/Cases content field
const ContentArrayEditor = ({ value, onChange }) => {
  const items = Array.isArray(value) ? value : [];
  
  const updateItem = (index, nextVal) => {
    const next = items.slice();
    next[index] = nextVal;
    onChange(next);
  };
  
  const removeItem = (index) => {
    const next = items.slice();
    next.splice(index, 1);
    onChange(next);
  };
  
  const addItem = (type = 'text') => {
    onChange([...items, { type, content: '' }]);
  };
  
  const moveItem = (index, direction) => {
    if (direction === 'up' && index > 0) {
      const next = items.slice();
      [next[index - 1], next[index]] = [next[index], next[index - 1]];
      onChange(next);
    } else if (direction === 'down' && index < items.length - 1) {
      const next = items.slice();
      [next[index], next[index + 1]] = [next[index + 1], next[index]];
      onChange(next);
    }
  };

  return (
    <div className="content-array-editor border rounded p-2 bg-light">
      {items.length === 0 && <div className="text-muted small mb-2">(empty content array)</div>}
      {items.map((item, idx) => {
        const itemValue = item && typeof item === 'object' ? item : { type: 'text', content: String(item || '') };
        const itemType = itemValue.type || 'text';
        const itemContent = itemValue.content || '';
        
        return (
          <div key={idx} className="content-item-row border rounded p-2 mb-2 bg-white">
            <div className="d-flex align-items-start gap-2 mb-2">
              <div className="d-flex flex-column gap-1">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => moveItem(idx, 'up')}
                  disabled={idx === 0}
                  title="Move up"
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => moveItem(idx, 'down')}
                  disabled={idx === items.length - 1}
                  title="Move down"
                >
                  ↓
                </button>
              </div>
              <select
                className="form-select form-select-sm"
                style={{ width: '120px' }}
                value={itemType}
                onChange={(e) => updateItem(idx, { ...itemValue, type: e.target.value })}
              >
                <option value="text">Text</option>
                <option value="image">Image</option>
              </select>
              <div className="flex-grow-1">
                {itemType === 'text' ? (
                  <textarea
                    className="form-control form-control-sm"
                    rows={3}
                    value={itemContent}
                    onChange={(e) => updateItem(idx, { ...itemValue, content: e.target.value })}
                    placeholder="Enter text content..."
                  />
                ) : (
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={itemContent}
                    onChange={(e) => updateItem(idx, { ...itemValue, content: e.target.value })}
                    placeholder="Enter image URL..."
                  />
                )}
              </div>
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={() => removeItem(idx)}
              >
                Delete
              </button>
            </div>
            {itemType === 'image' && itemContent && (
              <div className="mt-2">
                <img src={itemContent} alt="Preview" style={{ maxWidth: '200px', maxHeight: '150px', objectFit: 'contain' }} onError={(e) => { e.target.style.display = 'none'; }} />
              </div>
            )}
          </div>
        );
      })}
      <div className="d-flex gap-2">
        <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => addItem('text')}>+ Add Text</button>
        <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => addItem('image')}>+ Add Image</button>
      </div>
    </div>
  );
};

// Fixed item editor for News with locked schema
const FixedItemEditor = ({ value, onChange }) => {
  const v = value || {};
  const id = Number.isFinite(v.id) ? v.id : parseInt(v.id, 10) || 0;
  const title = v.title ?? '';
  const date = (() => { const s = String(v.date ?? '').trim(); return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : todayStr(); })();
  const excerpt = v.excerpt ?? '';
  const content = Array.isArray(v.content) ? v.content : (v.content ? [{ type: 'text', content: String(v.content) }] : []);
  const image = v.image ?? '';

  const patch = (p) => onChange({ ...v, ...p, id });

  return (
    <div className="json-object editor-block border rounded p-2 bg-light">
      <div className="json-field-row d-flex align-items-start gap-2 mb-2">
        <span className="json-key-label">ID</span>
        <div className="json-value flex-grow-1 d-flex align-items-center" aria-label="ID value">
          <span className="form-text">{id}</span>
        </div>
      </div>
      <div className="json-field-row d-flex align-items-start gap-2 mb-2">
        <span className="json-key-label">Title</span>
        <div className="json-value flex-grow-1">
          <input type="text" className="form-control form-control-sm" value={title} onChange={(e) => patch({ title: e.target.value })} />
        </div>
      </div>
      <div className="json-field-row d-flex align-items-start gap-2 mb-2">
        <span className="json-key-label">Date</span>
        <div className="json-value flex-grow-1">
          <input type="date" className="form-control form-control-sm" value={date} onChange={(e) => patch({ date: e.target.value })} />
        </div>
      </div>
      <div className="json-field-row d-flex align-items-start gap-2 mb-2">
        <span className="json-key-label">Excerpt</span>
        <div className="json-value flex-grow-1">
          <input type="text" className="form-control form-control-sm" value={excerpt} onChange={(e) => patch({ excerpt: e.target.value })} />
        </div>
      </div>
      <div className="json-field-row d-flex align-items-start gap-2 mb-2">
        <span className="json-key-label">Content</span>
        <div className="json-value flex-grow-1">
          <ContentArrayEditor value={content} onChange={(newContent) => patch({ content: newContent })} />
        </div>
      </div>
      <div className="json-field-row d-flex align-items-start gap-2 mb-2">
        <span className="json-key-label">Image</span>
        <div className="json-value flex-grow-1">
          <input type="text" className="form-control form-control-sm" value={image} onChange={(e) => patch({ image: e.target.value })} />
        </div>
      </div>
    </div>
  );
};

const LocalJsonEditor = ({ title, initialValue, onSave, filename, fieldTypeHints, lockSchema = false }) => {
  const [value, setValue] = useState(() => deepClone(initialValue));
  const [savedOnce, setSavedOnce] = useState(false);
  useEffect(() => { setValue(deepClone(initialValue)); setSavedOnce(false); }, [initialValue]);
  
  // Check if form data has changed
  const hasChanges = useMemo(() => {
    return JSON.stringify(value) !== JSON.stringify(initialValue);
  }, [value, initialValue]);
  
  const save = async () => {
    try {
      if (onSave) await onSave(deepClone(value));
      setSavedOnce(true);
      if (typeof window !== 'undefined' && window.location) {
        window.location.reload();
      }
    } catch (_) { setSavedOnce(false); }
  };
  return (
    <div className="json-editor card shadow-sm mb-3">
      <div className="card-body">
        <FixedItemEditor value={value} onChange={setValue} />
        <div className="d-flex justify-content-end align-items-center gap-2 mt-3 pt-3 border-top">
          {savedOnce && <div className="text-success small me-2">已儲存。</div>}
          <button 
            type="button" 
            className="btn btn-sm btn-primary" 
            onClick={save}
            disabled={!hasChanges}
          >
            儲存變更
          </button>
          </div>
        </div>
      </div>
  );
};
// Tag Input Component for Keywords
const KeywordsTagInput = ({ value = [], onChange, placeholder = "添加關鍵詞..." }) => {
  const [inputValue, setInputValue] = useState('');

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addKeyword();
    } else if (e.key === 'Backspace' && inputValue === '' && value.length > 0) {
      // Remove last tag when backspace is pressed on empty input
      removeKeyword(value.length - 1);
    }
  };

  const addKeyword = () => {
    const trimmed = inputValue.trim();
    if (trimmed && !value.includes(trimmed)) {
      onChange([...value, trimmed]);
      setInputValue('');
    } else if (trimmed && value.includes(trimmed)) {
      // Keyword already exists, just clear input
      setInputValue('');
    }
  };

  const removeKeyword = (index) => {
    const newKeywords = value.filter((_, i) => i !== index);
    onChange(newKeywords);
  };

  const handleInputChange = (e) => {
    const val = e.target.value;
    // Allow comma to trigger add, but don't add it to input
    if (val.endsWith(',')) {
      setInputValue(val.slice(0, -1));
      setTimeout(() => addKeyword(), 0);
    } else {
      setInputValue(val);
    }
  };

  return (
    <div 
      className="keywords-tag-input"
      style={{
        border: '1px solid #ced4da',
        borderRadius: '0.25rem',
        padding: '0.25rem',
        minHeight: '31px',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.25rem',
        alignItems: 'center',
        backgroundColor: '#fff',
        cursor: 'text'
      }}
      onClick={(e) => {
        // Focus input when clicking on the container
        const input = e.currentTarget.querySelector('input');
        if (input) input.focus();
      }}
    >
      {value.map((keyword, index) => (
        <span
          key={index}
          className="badge bg-secondary"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.25rem',
            padding: '0.25rem 0.5rem',
            fontSize: '0.875rem'
          }}
        >
          {keyword}
          <button
            type="button"
            className="btn-close btn-close-white"
            style={{
              fontSize: '0.75rem',
              padding: '0',
              margin: '0',
              width: '0.75rem',
              height: '0.75rem'
            }}
            onClick={(e) => {
              e.stopPropagation();
              removeKeyword(index);
            }}
            aria-label="移除"
          ></button>
        </span>
      ))}
      <input
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        placeholder={value.length === 0 ? placeholder : ''}
        style={{
          border: 'none',
          outline: 'none',
          flex: 1,
          minWidth: '100px',
          fontSize: '0.875rem',
          padding: '0.25rem'
        }}
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );
};

// NewsTask editor component
const NewsTaskEditor = () => {
  const [list, setList] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [newKeywords, setNewKeywords] = useState([]);
  const [newTargetLanguage, setNewTargetLanguage] = useState('zh-HK');
  const [newSource, setNewSource] = useState('news');
  const [message, setMessage] = useState('');
  const [generating, setGenerating] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [taskRecords, setTaskRecords] = useState({}); // { taskId: [records] }
  const [selectedTaskId, setSelectedTaskId] = useState(null); // Track which task is selected for viewing records
  const [taskRecordsPage, setTaskRecordsPage] = useState(1); // Current page for task records pagination
  const taskRecordsPerPage = 10; // Records per page

  const refresh = async (keepSelected = true) => {
    const l = await newsTaskService.getListData();
    setList(Array.isArray(l) ? l : []);
    if (!keepSelected) {
      if (Array.isArray(l) && l.length > 0) setSelectedId(String(l[0].id));
      else setSelectedId('');
    }
  };

  const fetchTaskRecords = async (taskId) => {
    const taskIdStr = String(taskId);
    try {
      const queryParams = new URLSearchParams({
        filterKey: 'taskId',
        filterValue: taskIdStr
      });
      const response = await api.get(`/newsTaskRecord?${queryParams.toString()}`);
      const records = Array.isArray(response) ? response : (response.items || []);
      console.log(`Fetched ${records.length} records for task ${taskIdStr}`, records);
      setTaskRecords(prev => ({ ...prev, [taskIdStr]: records }));
    } catch (error) {
      console.error('Failed to fetch task records:', error);
      setTaskRecords(prev => ({ ...prev, [taskIdStr]: [] }));
    }
  };

  const handleRefresh = async () => {
    // Refresh the task list
      await refresh(true);
    // Refresh task records for selected task
    if (selectedTaskId) {
      await fetchTaskRecords(selectedTaskId);
    }
      setMessage('已刷新。');
      setTimeout(() => setMessage(''), 1500);
  };

  useEffect(() => {
    (async () => {
      await refresh(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset task records page when selected task changes
  useEffect(() => {
    setTaskRecordsPage(1);
  }, [selectedTaskId]);

  const current = useMemo(() => {
    if (!selectedId) return null;
    return (list || []).find((it) => String(it.id) === String(selectedId)) || null;
  }, [selectedId, list]);

  const handleRowClick = async (taskId) => {
    const taskIdStr = String(taskId);
    console.log('Row clicked, taskId:', taskId, 'taskIdStr:', taskIdStr, 'current selectedTaskId:', selectedTaskId);
    if (selectedTaskId === taskIdStr) {
      // If clicking the same row, deselect it
      console.log('Deselecting task');
      setSelectedTaskId(null);
    } else {
      // Select the clicked row and fetch records if needed
      console.log('Selecting task:', taskIdStr, 'existing records:', taskRecords[taskIdStr]);
      setSelectedTaskId(taskIdStr);
      if (!taskRecords[taskIdStr]) {
        console.log('Fetching records for task:', taskIdStr);
        await fetchTaskRecords(taskId);
      } else {
        console.log('Records already loaded:', taskRecords[taskIdStr]);
      }
    }
  };

  const handleSave = async (taskId, next) => {
    if (!taskId) return;
    try {
      await newsTaskService.updateListObjectDataById(taskId, next || {});
      await refresh(true);
      setMessage('已更新。');
      setTimeout(() => setMessage(''), 1500);
    } catch (error) {
      setMessage('更新失敗');
      setTimeout(() => setMessage(''), 1500);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('確定要刪除此任務嗎？')) return;
    try {
      await newsTaskService.deleteObjectInArrayData(id);
      await refresh(true);
      const filtered = list.filter(it => String(it.id) !== String(id));
      if (filtered.length > 0) setSelectedId(String(filtered[0].id));
      else setSelectedId('');
      setMessage('已刪除。');
      setTimeout(() => setMessage(''), 1500);
    } catch (error) {
      setMessage('刪除失敗');
      setTimeout(() => setMessage(''), 1500);
    }
  };

  const handleCreate = async () => {
    // Allow empty keywords array
    const keywords = Array.isArray(newKeywords) ? newKeywords.filter(k => k && k.trim()) : [];
    try {
      const newItem = {
        keywords,
        targetLanguage: newTargetLanguage,
        source: newSource,
        active: true, // Default to active
        createdAt: new Date().toISOString().replace('T', '  ').substring(0, 19),
      };
      const inserted = await newsTaskService.insertObjectToArrayData(newItem);
      await refresh(true);
      if (inserted && inserted.id != null) {
        setSelectedId(String(inserted.id));
        // Fetch records for the new task
        fetchTaskRecords(inserted.id);
      }
      setNewKeywords([]);
      setNewTargetLanguage('zh-HK');
      setNewSource('news');
      setShowCreateForm(false);
      setMessage('已創建。');
      setTimeout(() => setMessage(''), 1500);
    } catch (error) {
      setMessage('創建失敗');
      setTimeout(() => setMessage(''), 1500);
    }
  };

  const handleGenerateNews = async (taskId, task) => {
    if (!taskId || !task) {
      setMessage('請先選擇任務。');
      setTimeout(() => setMessage(''), 2000);
      return;
    }
    
    if (!window.confirm(`這將為任務 ${taskId} (${(task.keywords || []).join(', ')}) 生成新聞文章。這可能需要幾分鐘。繼續嗎？`)) return;
    
    setGenerating(true);
    setMessage(`正在為任務 ${taskId} 生成新聞文章...`);
    try {
      const response = await api.post('/news-generator', {
        newTaskId: taskId
      });
      
      // Parse response body if it's a string
      let responseData = response;
      if (typeof response === 'string') {
        try {
          responseData = JSON.parse(response);
        } catch (e) {
          // If parsing fails, use the string response
        }
      }
      
      // Extract message from response
      const responseBody = responseData.body ? (typeof responseData.body === 'string' ? JSON.parse(responseData.body) : responseData.body) : responseData;
      const message = responseBody.message || `生成完成！任務 ${taskId} 已處理。`;
      
      setMessage(message);
      
      // Refresh task records after generation
      if (taskId) {
        await fetchTaskRecords(taskId);
      }
      
      setTimeout(() => {
        setMessage('');
        setGenerating(false);
      }, 5000);
    } catch (error) {
      console.error('Generation failed:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Unknown error';
      setMessage(`生成失敗：${errorMessage}`);
      setTimeout(() => {
        setMessage('');
        setGenerating(false);
      }, 5000);
    }
  };

  const options = useMemo(() => (list || []).map(it => ({ id: String(it.id), label: `${it.id} – ${(it.keywords || []).join(', ')}` })), [list]);

  return (
    <div>
      <div className="mb-2 d-flex align-items-center gap-2 flex-wrap">
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          onClick={() => setShowCreateForm(!showCreateForm)}
        >
          {showCreateForm ? '− 取消' : '+ 新增任務'}
        </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={handleRefresh}
                title="刷新任務數據和記錄"
              >
                <span>↻</span> 刷新
              </button>
            </div>
      {message && <div className={`text-${message.includes('failed') ? 'danger' : 'success'} small mb-2`}>{message}</div>}
      
      {/* Tasks Table */}
      <div className="card shadow-sm mb-3">
        <div className="card-body">
          <h5 className="mb-3">新聞任務</h5>
          {list.length === 0 ? (
            <p className="text-muted small mb-0">未找到任務。創建新任務以開始。</p>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm table-bordered">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>關鍵詞</th>
                    <th>目標語言</th>
                    <th>來源</th>
                    <th>啟用</th>
                    <th>創建時間</th>
                    <th>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((task) => (
                    <tr 
                      key={task.id}
                      onClick={() => handleRowClick(task.id)}
                      style={{ 
                        cursor: 'pointer',
                        backgroundColor: selectedTaskId === String(task.id) ? '#e7f3ff' : 'transparent'
                      }}
                      onMouseEnter={(e) => {
                        if (selectedTaskId !== String(task.id)) {
                          e.currentTarget.style.backgroundColor = '#f8f9fa';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedTaskId !== String(task.id)) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                    >
                      <td>{task.id}</td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <KeywordsTagInput
                          value={task.keywords || []}
                          onChange={(keywords) => {
                            handleSave(String(task.id), { ...task, keywords });
                }}
                placeholder="e.g., 香港, 留学, 教育"
              />
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
              <select
                className="form-select form-select-sm"
                          value={task.targetLanguage || 'zh-HK'}
                          onChange={(e) => handleSave(String(task.id), { ...task, targetLanguage: e.target.value })}
              >
                <option value="zh-HK">zh-HK</option>
                <option value="zh-CN">zh-CN</option>
                <option value="en">en</option>
              </select>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
              <select
                className="form-select form-select-sm"
                          value={task.source || 'news'}
                          onChange={(e) => handleSave(String(task.id), { ...task, source: e.target.value })}
              >
                <option value="news">新聞 (NewsAPI)</option>
                <option value="school">學校 (大學爬蟲)</option>
                <option value="finance">金融 (HKMA API)</option>
              </select>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <select
                          className="form-select form-select-sm"
                          value={task.active === true ? 'true' : 'false'}
                          onChange={(e) => handleSave(String(task.id), { ...task, active: e.target.value === 'true' })}
                        >
                          <option value="true">啟用</option>
                          <option value="false">停用</option>
                        </select>
                      </td>
                      <td>
              <input
                type="text"
                className="form-control form-control-sm"
                          value={task.createdAt || ''}
                readOnly
                disabled
                          style={{ minWidth: '150px' }}
              />
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="d-flex gap-1 flex-wrap">
              <button
                type="button"
                className="btn btn-sm btn-success"
                            onClick={() => handleGenerateNews(String(task.id), task)}
                            disabled={generating}
                            title="生成新聞文章"
              >
                            {generating ? '生成中...' : '生成'}
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(task.id)}
                            title="刪除任務"
                          >
                            刪除
              </button>
            </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      
      {/* Task Records - Separate card below the tasks table */}
      {selectedTaskId && (() => {
        const records = taskRecords[selectedTaskId];
        console.log('Rendering Task Records card, selectedTaskId:', selectedTaskId, 'records:', records, 'isArray:', Array.isArray(records));
        
        // Sort records by createdAt (newest first)
        const sortedRecords = records && Array.isArray(records) ? [...records].sort((a, b) => {
          const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
          const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
          return dateB - dateA; // Descending order (newest first)
        }) : [];
        
        // Calculate pagination
        const totalPages = Math.ceil(sortedRecords.length / taskRecordsPerPage);
        const startIndex = (taskRecordsPage - 1) * taskRecordsPerPage;
        const endIndex = startIndex + taskRecordsPerPage;
        const paginatedRecords = sortedRecords.slice(startIndex, endIndex);
        
        return (
          <div className="card shadow-sm mb-3">
            <div className="card-body">
              {records && Array.isArray(records) && records.length > 0 ? (
              <>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="mb-0">任務 {selectedTaskId} 的記錄 ({records.length})</h6>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => {
                      setSelectedTaskId(null);
                      setTaskRecordsPage(1);
                    }}
                    title="關閉"
                  >
                    ×
                  </button>
                </div>
                <div className="table-responsive">
                  <table className="table table-sm table-bordered">
                    <thead>
                      <tr>
                        <th>記錄 ID</th>
                        <th>創建時間</th>
                        <th>狀態</th>
                        <th>文章</th>
                        <th>操作</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedRecords.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="text-center text-muted py-4">
                            未找到記錄
                          </td>
                        </tr>
                      ) : (
                        paginatedRecords.map((record) => (
                        <tr key={record.id}>
                          <td>{record.id}</td>
                          <td>{record.createdAt || '-'}</td>
                          <td>
                            {(() => {
                              const status = record.status || 'completed';
                              if (status === 'in_progress') {
                                return <span className="badge bg-warning">進行中</span>;
                              } else if (status === 'failed') {
                                return <span className="badge bg-danger">失敗</span>;
                              } else if (status === 'completed') {
                                const articleCount = record.articles?.length || 0;
                                return <span className="badge bg-success">已完成 ({articleCount})</span>;
                              } else {
                                const articleCount = record.articles?.length || 0;
                                return articleCount > 0 ? (
                                  <span className="badge bg-success">已完成 ({articleCount})</span>
                                ) : (
                                  <span className="badge bg-secondary">無文章</span>
                                );
                              }
                            })()}
                          </td>
                          <td>
                            {record.articles && record.articles.length > 0 ? (
                              <ul className="mb-0" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                {record.articles.map((article, idx) => (
                                  <li key={idx} className="small">
                                    <a href={article.url} target="_blank" rel="noopener noreferrer" className="text-break">
                                      {article.title || article.url}
                                    </a>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <span className="text-muted small">無文章</span>
                            )}
                          </td>
                          <td>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={async () => {
                                if (!window.confirm(`確定要刪除記錄 ${record.id} 嗎？`)) return;
                                try {
                                  await newsTaskRecordService.deleteObjectInArrayData(record.id);
                                  await fetchTaskRecords(selectedTaskId);
                                  setMessage('記錄已刪除。');
                                  setTimeout(() => setMessage(''), 1500);
                                } catch (error) {
                                  console.error('Delete failed:', error);
                                  setMessage('刪除失敗');
                                  setTimeout(() => setMessage(''), 1500);
                                }
                              }}
                            >
                              刪除
                            </button>
                          </td>
                        </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                {/* Pagination */}
                {totalPages > 1 && (
                  <nav aria-label="任務記錄分頁">
                    <ul className="pagination pagination-sm justify-content-center mt-3 mb-0">
                      <li className={`page-item ${taskRecordsPage === 1 ? 'disabled' : ''}`}>
                        <button 
                          className="page-link" 
                          onClick={() => setTaskRecordsPage(prev => Math.max(1, prev - 1))}
                          disabled={taskRecordsPage === 1}
                        >
                          上一頁
                        </button>
                      </li>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                        <li key={page} className={`page-item ${taskRecordsPage === page ? 'active' : ''}`}>
                          <button className="page-link" onClick={() => setTaskRecordsPage(page)}>
                            {page}
                          </button>
                        </li>
                      ))}
                      <li className={`page-item ${taskRecordsPage === totalPages ? 'disabled' : ''}`}>
                        <button 
                          className="page-link" 
                          onClick={() => setTaskRecordsPage(prev => Math.min(totalPages, prev + 1))}
                          disabled={taskRecordsPage === totalPages}
                        >
                          下一頁
                        </button>
                      </li>
                    </ul>
                  </nav>
                )}
              </>
            ) : (
              <div className="d-flex justify-content-between align-items-center">
                <p className="text-muted small mb-0">
                  {records === undefined 
                    ? '載入記錄中...' 
                    : '未找到此任務的記錄。'}
                </p>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setSelectedTaskId(null)}
                  title="關閉"
                >
                  ×
                </button>
              </div>
            )}
          </div>
        </div>
        );
      })()}
      
      {/* Create New Task Modal */}
      {showCreateForm && (
        <>
          {/* Modal Backdrop */}
          <div 
            className="modal-backdrop show" 
            onClick={() => setShowCreateForm(false)}
            style={{ zIndex: 1040 }}
          ></div>
          
          {/* Modal */}
          <div 
            className="modal show d-block" 
            tabIndex="-1" 
            role="dialog"
            style={{ zIndex: 1050 }}
          >
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">創建新任務</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowCreateForm(false)}
                    aria-label="關閉"
                  ></button>
                </div>
                <div className="modal-body">
            <div className="mb-3">
                    <label className="form-label">關鍵詞（可選）</label>
                    <KeywordsTagInput
                value={newKeywords}
                      onChange={setNewKeywords}
                placeholder="e.g., 香港, 留学, 教育"
              />
            </div>
            <div className="mb-3">
              <label className="form-label">目標語言</label>
              <select
                      className="form-select"
                value={newTargetLanguage}
                onChange={(e) => setNewTargetLanguage(e.target.value)}
              >
                <option value="zh-HK">zh-HK</option>
                <option value="zh-CN">zh-CN</option>
                <option value="en">en</option>
              </select>
            </div>
            <div className="mb-3">
              <label className="form-label">來源</label>
              <select
                      className="form-select"
                value={newSource}
                onChange={(e) => setNewSource(e.target.value)}
              >
                <option value="news">新聞 (NewsAPI)</option>
                <option value="school">學校 (大學爬蟲)</option>
                      <option value="finance">金融 (HKMA API)</option>
              </select>
            </div>
          </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowCreateForm(false)}
                  >
                    取消
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleCreate}
                  >
                    創建任務
                  </button>
        </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// Wrapper: loads list, lets user pick an item to edit, saves via newsService
const NewsDataEditor = ({ filename }) => {
  const [list, setList] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [filter, setFilter] = useState('all'); // 'all', 'active', 'deleted'
  const [activeTab, setActiveTab] = useState('news'); // 'news', 'tasks', or 'today'
  const [currentPage, setCurrentPage] = useState(1);
  const [showEditModal, setShowEditModal] = useState(false);
  const itemsPerPage = 10;
  const [todayNews, setTodayNews] = useState([]); // Today's news articles
  const [loadingTodayNews, setLoadingTodayNews] = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  }); // Selected date for filtering news
  const [selectedArticleForDescription, setSelectedArticleForDescription] = useState(null); // Article to show description modal
  const [selectedArticleUrls, setSelectedArticleUrls] = useState(new Set()); // Selected article URLs for generation
  const [generatingArticles, setGeneratingArticles] = useState(false); // Loading state for article generation
  const [message, setMessage] = useState(''); // Message for user feedback
  const [showTopicInputModal, setShowTopicInputModal] = useState(false); // Show topic input modal
  const [pendingArticleStyle, setPendingArticleStyle] = useState(null); // Article style pending topic input
  const [topicTo, setTopicTo] = useState(''); // Topic to align article with

  const refresh = async (keepSelected = true) => {
    const l = await newsService.getListData();
    setList(Array.isArray(l) ? l : []);
    if (!keepSelected) {
      const filtered = getFilteredList(Array.isArray(l) ? l : []);
      if (filtered.length > 0) setSelectedId(String(filtered[0].id));
      else setSelectedId('');
    }
  };

  const getFilteredList = (items) => {
    if (filter === 'active') return items.filter(it => !it.isDeleted);
    if (filter === 'deleted') return items.filter(it => it.isDeleted);
    return items;
  };

  // Fetch today's news records
  const fetchTodayNews = async (dateStr = null) => {
    setLoadingTodayNews(true);
    try {
      const [allRecords, allTasks] = await Promise.all([
        newsTaskRecordService.getListData(),
        newsTaskService.getListData()
      ]);
      const targetDate = dateStr || selectedDate;
      
      // Create a map of taskId to formatted "任務來源（關鍵詞）" string
      const taskMap = {};
      (Array.isArray(allTasks) ? allTasks : []).forEach(task => {
        const taskIdStr = String(task.id);
        const source = task.source || 'news';
        const keywords = task.keywords || [];
        
        // Map source to Chinese
        const sourceMap = {
          'news': '新聞',
          'school': '學校',
          'finance': '金融'
        };
        const sourceText = sourceMap[source] || source;
        
        // Format: 來源（關鍵詞1, 關鍵詞2, ...）
        if (keywords.length > 0) {
          taskMap[taskIdStr] = `${sourceText}（${keywords.join(', ')}）`;
        } else {
          taskMap[taskIdStr] = sourceText;
        }
      });
      
      // Filter records created on the selected date
      const dateRecords = (Array.isArray(allRecords) ? allRecords : []).filter(record => {
        if (!record.createdAt) return false;
        // Handle different date formats: "2025-09-16 21:26:53" or ISO format
        const recordDate = record.createdAt.split(' ')[0].split('T')[0];
        return recordDate === targetDate;
      });
      
      // Flatten articles from all records on the selected date
      const articles = [];
      dateRecords.forEach(record => {
        if (record.articles && Array.isArray(record.articles)) {
          const taskIdStr = String(record.taskId);
          // Get formatted task info from taskMap, or use default
          const taskInfo = taskMap[taskIdStr] || '新聞';
          
          record.articles.forEach(article => {
            articles.push({
              ...article,
              recordId: record.id,
              taskId: record.taskId,
              taskInfo: taskInfo, // Store formatted string directly
              recordCreatedAt: record.createdAt,
              recordStatus: record.status
            });
          });
        }
      });
      
      setTodayNews(articles);
    } catch (error) {
      console.error('Error fetching today\'s news:', error);
      setTodayNews([]);
    } finally {
      setLoadingTodayNews(false);
    }
  };

  useEffect(() => {
    (async () => {
      await refresh(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch news when switching to today tab or when selected date changes
  useEffect(() => {
    if (activeTab === 'today') {
      fetchTodayNews(selectedDate);
    }
  }, [activeTab, selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle article selection checkbox
  const handleArticleSelect = (url) => {
    const newSelected = new Set(selectedArticleUrls);
    if (newSelected.has(url)) {
      newSelected.delete(url);
    } else {
      newSelected.add(url);
    }
    setSelectedArticleUrls(newSelected);
  };

  // Handle select all/none
  const handleSelectAll = () => {
    if (selectedArticleUrls.size === todayNews.length) {
      setSelectedArticleUrls(new Set());
    } else {
      const allUrls = new Set(todayNews.map(article => article.url).filter(Boolean));
      setSelectedArticleUrls(allUrls);
    }
  };

  // Handle article style button click - show topic input modal first
  const handleArticleStyleClick = (articleStyle) => {
    if (selectedArticleUrls.size === 0) {
      setMessage('請至少選擇一篇文章。');
      setTimeout(() => setMessage(''), 2000);
      return;
    }
    
    // Set pending style and show modal
    setPendingArticleStyle(articleStyle);
    setTopicTo(''); // Reset topic input
    setShowTopicInputModal(true);
  };

  // Generate articles using article-rewriter Lambda (called after topic input confirmation)
  const handleGenerateArticles = async () => {
    if (!pendingArticleStyle) {
      return;
    }

    const urls = Array.from(selectedArticleUrls);
    
    // Close modal
    setShowTopicInputModal(false);
    const articleStyle = pendingArticleStyle;
    setPendingArticleStyle(null);

    setGeneratingArticles(true);
    try {
      // Prepare request body
      const requestBody = {
        urls: urls,
        output_language: 'zh-HK', // Default language, can be made configurable
        article_style: articleStyle
      };
      
      // Add topicTo if provided
      if (topicTo && topicTo.trim()) {
        requestBody.topicTo = topicTo.trim();
      }

      // Call article-rewriter API
      const response = await api.post('/article-rewriter', requestBody);

      // Check if the response indicates processing started
      if (response && (response.status === 'processing' || response.message?.includes('started'))) {
        setMessage(`文章生成任務已啟動，完成後將保存到新聞數據中。`);
        setTimeout(() => setMessage(''), 5000);
        // Clear selection after successful submission
        setSelectedArticleUrls(new Set());
        // Reset topic input
        setTopicTo('');
        // Refresh news data after a delay to show new articles
        setTimeout(() => {
          refresh(false);
        }, 10000); // Wait 10 seconds for processing to complete
      } else {
        setMessage('未能啟動文章生成任務，請檢查 API 配置。');
        setTimeout(() => setMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error generating articles:', error);
      setMessage('錯誤：未能生成文章');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setGeneratingArticles(false);
    }
  };

  // Handle cancel topic input
  const handleCancelTopicInput = () => {
    setShowTopicInputModal(false);
    setPendingArticleStyle(null);
    setTopicTo('');
  };

  const filteredList = useMemo(() => {
    const filtered = getFilteredList(list);
    // Sort by ID in descending order (11, 10, 9, 8, 7, ...)
    return [...filtered].sort((a, b) => {
      const idA = Number.isFinite(a.id) ? a.id : parseInt(a.id, 10) || 0;
      const idB = Number.isFinite(b.id) ? b.id : parseInt(b.id, 10) || 0;
      return idB - idA; // Descending order
    });
  }, [list, filter]);
  
  // Pagination logic
  const totalPages = Math.ceil(filteredList.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedList = useMemo(() => filteredList.slice(startIndex, endIndex), [filteredList, startIndex, endIndex]);
  
  // Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filter]);

  // Update selectedId when filter changes if current selection is not in filtered list
  useEffect(() => {
    if (selectedId && !filteredList.find(it => String(it.id) === String(selectedId))) {
      if (filteredList.length > 0) {
        setSelectedId(String(filteredList[0].id));
      } else {
        setSelectedId('');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, filteredList]);

  const current = useMemo(() => {
    if (!selectedId) return null;
    // If selectedId is 'new', return a new empty item
    if (selectedId === 'new') {
      return { title: '', date: todayStr(), excerpt: '', content: [], image: '', isDeleted: false };
    }
    return (list || []).find((it) => String(it.id) === String(selectedId)) || null;
  }, [selectedId, list]);

  const handleSave = async (next) => {
    if (!selectedId) return;
    // If selectedId is 'new', insert as new item
    if (selectedId === 'new') {
      const inserted = await newsService.insertObjectToArrayData(next || {});
      await refresh(true);
      if (inserted && inserted.id != null) {
        setSelectedId(String(inserted.id));
      } else {
        setSelectedId('');
      }
    } else {
    await newsService.updateListObjectDataById(selectedId, next || {});
    await refresh(true);
    }
    setShowEditModal(false);
  };
  
  const handleRowClick = (id) => {
    setSelectedId(String(id));
    setShowEditModal(true);
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
    // Reset selectedId if it was 'new' (user cancelled creating new item)
    if (selectedId === 'new') {
      setSelectedId('');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to permanently delete this item? This action cannot be undone.')) return;
    try {
      await newsService.deleteObjectInArrayData(id);
      await refresh(true);
      // If deleted item was selected, select first available item
      const filtered = getFilteredList(list.filter(it => String(it.id) !== String(id)));
      if (filtered.length > 0) setSelectedId(String(filtered[0].id));
      else setSelectedId('');
      // Close modal if the deleted item was being edited
      if (String(selectedId) === String(id)) {
        setShowEditModal(false);
        setSelectedId('');
      }
    } catch (error) {
      console.error('Delete failed:', error);
      alert('Delete failed');
    }
  };
  
  const handleToggleStatus = async (id) => {
    const item = list.find(it => String(it.id) === String(id));
    if (!item) return;
    const newStatus = !item.isDeleted;
    try {
      await newsService.updateListObjectDataById(id, { isDeleted: newStatus });
      await refresh(true);
    } catch (error) {
      console.error('Toggle status failed:', error);
      alert('Toggle status failed');
    }
  };

  const title = useMemo(() => {
    const base = 'News Data';
    if (!current) return base;
    const label = current.title || current.label || '';
    return `${base} — Item ${current.id}${label ? ` (${label})` : ''}`;
  }, [filename, current]);

  return (
    <div>
      {/* Tab Navigation */}
      <ul className="nav nav-tabs mb-3" role="tablist">
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link ${activeTab === 'news' ? 'active' : ''}`}
            onClick={() => setActiveTab('news')}
            type="button"
          >
            新聞數據
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link ${activeTab === 'tasks' ? 'active' : ''}`}
            onClick={() => setActiveTab('tasks')}
            type="button"
          >
            新聞任務
          </button>
        </li>
        <li className="nav-item" role="presentation">
          <button
            className={`nav-link ${activeTab === 'today' ? 'active' : ''}`}
            onClick={() => setActiveTab('today')}
            type="button"
          >
            新闻
          </button>
        </li>
      </ul>

      {/* News Tab */}
      {activeTab === 'news' && (
        <div>
          <div className="mb-2 d-flex align-items-center gap-2 flex-wrap">
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={() => {
                setSelectedId('new');
                setShowEditModal(true);
              }}
            >
              + 新增新聞
            </button>
            <div className="btn-group" role="group">
              <input
                type="radio"
                className="btn-check"
                name="filter"
                id="filter-all"
                checked={filter === 'all'}
                onChange={() => setFilter('all')}
              />
              <label className="btn btn-sm btn-outline-secondary" htmlFor="filter-all">全部</label>
              
              <input
                type="radio"
                className="btn-check"
                name="filter"
                id="filter-active"
                checked={filter === 'active'}
                onChange={() => setFilter('active')}
              />
              <label className="btn btn-sm btn-outline-secondary" htmlFor="filter-active">啟用</label>
              
              <input
                type="radio"
                className="btn-check"
                name="filter"
                id="filter-deleted"
                checked={filter === 'deleted'}
                onChange={() => setFilter('deleted')}
              />
              <label className="btn btn-sm btn-outline-secondary" htmlFor="filter-deleted">已刪除</label>
            </div>
          </div>
          {/* Table view */}
          <div className="table-responsive">
            <table className="table table-sm table-bordered table-hover">
              <thead>
                <tr>
                  <th style={{ width: '80px' }}>ID</th>
                  <th>標題</th>
                  <th style={{ width: '120px' }}>日期</th>
                  <th style={{ width: '100px' }}>狀態</th>
                  <th style={{ width: '220px' }}>操作</th>
                </tr>
              </thead>
              <tbody>
                {paginatedList.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center text-muted py-4">
                      未找到新聞
                    </td>
                  </tr>
                ) : (
                  paginatedList.map((item) => (
                    <tr 
                      key={item.id} 
                      style={{ cursor: 'pointer' }}
                      onClick={() => handleRowClick(item.id)}
                    >
                      <td>{item.id}</td>
                      <td>{item.title || '(無標題)'}</td>
                      <td>{item.date || '-'}</td>
                      <td>
                        <span className={`badge ${item.isDeleted ? 'bg-secondary' : 'bg-success'}`}>
                          {item.isDeleted ? '已刪除' : '啟用'}
                        </span>
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        <div className="d-flex gap-1">
                          <button
                            type="button"
                            className={`btn btn-sm ${item.isDeleted ? 'btn-outline-success' : 'btn-outline-warning'}`}
                            onClick={() => handleToggleStatus(item.id)}
                            title={item.isDeleted ? '啟用此項目' : '停用此項目'}
                          >
                            {item.isDeleted ? '啟用' : '停用'}
                          </button>
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                            onClick={() => handleDelete(item.id)}
              >
                            刪除
              </button>
                        </div>
                      </td>
                    </tr>
                  ))
            )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (() => {
            // Calculate which page numbers to show
            const getPageNumbers = () => {
              const pages = [];
              const maxVisible = 7; // Maximum number of page buttons to show
              
              if (totalPages <= maxVisible) {
                // Show all pages if total pages is less than maxVisible
                for (let i = 1; i <= totalPages; i++) {
                  pages.push(i);
                }
              } else {
                // Show pages with ellipsis
                if (currentPage <= 3) {
                  // Show first pages
                  for (let i = 1; i <= 5; i++) {
                    pages.push(i);
                  }
                  pages.push('ellipsis');
                  pages.push(totalPages);
                } else if (currentPage >= totalPages - 2) {
                  // Show last pages
                  pages.push(1);
                  pages.push('ellipsis');
                  for (let i = totalPages - 4; i <= totalPages; i++) {
                    pages.push(i);
                  }
                } else {
                  // Show middle pages
                  pages.push(1);
                  pages.push('ellipsis');
                  for (let i = currentPage - 1; i <= currentPage + 1; i++) {
                    pages.push(i);
                  }
                  pages.push('ellipsis');
                  pages.push(totalPages);
                }
              }
              return pages;
            };
            
            const pageNumbers = getPageNumbers();
            
            return (
              <nav aria-label="新聞分頁" className="mt-3">
                <ul className="pagination pagination-sm justify-content-center">
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    >
                      上一頁
                    </button>
                  </li>
                  {pageNumbers.map((page, idx) => {
                    if (page === 'ellipsis') {
                      return (
                        <li key={`ellipsis-${idx}`} className="page-item disabled">
                          <span className="page-link">...</span>
                        </li>
                      );
                    }
                    return (
                      <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                        <button
                          className="page-link"
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </button>
                      </li>
                    );
                  })}
                  <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button
                      className="page-link"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                    >
                      下一頁
                    </button>
                  </li>
                </ul>
              </nav>
            );
          })()}
          
          {/* Edit Modal */}
          {showEditModal && current && (
            <>
              <div 
                className="modal fade show" 
                style={{ display: 'block', zIndex: 1050 }} 
                tabIndex="-1"
                role="dialog"
                aria-modal="true"
                onClick={(e) => {
                  if (e.target === e.currentTarget) handleCloseModal();
                }}
              >
                <div className="modal-dialog modal-lg modal-dialog-scrollable" onClick={(e) => e.stopPropagation()}>
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">{title}</h5>
                      <button
                        type="button"
                        className="btn-close"
                        onClick={handleCloseModal}
                        aria-label="關閉"
                      ></button>
                    </div>
                    <div className="modal-body">
            <LocalJsonEditor
              title={title}
              initialValue={current}
              filename={filename || 'newsData.json'}
              onSave={handleSave}
            />
                    </div>
                  </div>
                </div>
              </div>
              <div 
                className="modal-backdrop fade show" 
                style={{ zIndex: 1040 }}
                onClick={handleCloseModal}
              ></div>
            </>
          )}
        </div>
      )}

      {/* News Tasks Tab */}
      {activeTab === 'tasks' && <NewsTaskEditor />}

      {/* Today's News Tab */}
      {activeTab === 'today' && (
        <div>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="d-flex align-items-center gap-2">
              <label htmlFor="news-date-selector" className="mb-0 me-2">選擇日期：</label>
              <input
                type="date"
                id="news-date-selector"
                className="form-control form-control-sm"
                style={{ width: '180px' }}
                value={selectedDate}
                onChange={(e) => {
                  setSelectedDate(e.target.value);
                }}
              />
              <span className="text-muted small ms-2">({todayNews.length} 條新聞)</span>
            </div>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={() => fetchTodayNews(selectedDate)}
              disabled={loadingTodayNews}
            >
              {loadingTodayNews ? '載入中...' : '↻ 刷新'}
            </button>
          </div>
          {message && (
            <div className={`alert alert-${message.includes('failed') || message.includes('Error') ? 'danger' : 'success'} alert-dismissible fade show`} role="alert">
              {message}
              <button
                type="button"
                className="btn-close"
                onClick={() => setMessage('')}
                aria-label="Close"
              ></button>
            </div>
          )}
          {loadingTodayNews ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">載入中...</span>
              </div>
            </div>
          ) : todayNews.length === 0 ? (
            <div className="alert alert-info mb-0">
              未找到該日期的新聞文章。
            </div>
          ) : (
            <>
              <div className="mb-3 d-flex align-items-center gap-2 flex-wrap">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={handleSelectAll}
                >
                  {selectedArticleUrls.size === todayNews.length ? '取消全選' : '全選'}
                </button>
                <span className="text-muted small">
                  已選 {selectedArticleUrls.size} / {todayNews.length}
                </span>
                <div className="dropdown ms-auto">
                  <button
                    className="btn btn-sm btn-primary dropdown-toggle"
                    type="button"
                    id="generateArticleDropdown"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                    disabled={selectedArticleUrls.size === 0 || generatingArticles}
                  >
                    {generatingArticles ? '生成中...' : '生成文章'}
                  </button>
                  <ul className="dropdown-menu" aria-labelledby="generateArticleDropdown">
                    <li>
                      <button
                        className="dropdown-item"
                        onClick={() => handleArticleStyleClick('xiaohongshu')}
                        disabled={generatingArticles}
                      >
                        小红书笔记
                      </button>
                    </li>
                    <li>
                      <button
                        className="dropdown-item"
                        onClick={() => handleArticleStyleClick('news_summary')}
                        disabled={generatingArticles}
                      >
                        新闻总结
                      </button>
                    </li>
                    <li>
                      <button
                        className="dropdown-item"
                        onClick={() => handleArticleStyleClick('short_video_script')}
                        disabled={generatingArticles}
                      >
                        短视频script
                      </button>
                    </li>
                    <li>
                      <button
                        className="dropdown-item"
                        onClick={() => handleArticleStyleClick('wechat_article')}
                        disabled={generatingArticles}
                      >
                        微信公众号文章
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="table-responsive">
                <table className="table table-sm table-bordered table-hover">
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}>
                        <input
                          type="checkbox"
                          checked={selectedArticleUrls.size === todayNews.length && todayNews.length > 0}
                          onChange={handleSelectAll}
                          title="全選"
                        />
                      </th>
                      <th>頁面</th>
                      <th>任務來源（關鍵詞）</th>
                      <th>創建時間</th>
                    </tr>
                  </thead>
                  <tbody>
                    {todayNews.map((article, index) => {
                      // Combine description and content for display
                      const description = article.description || '';
                      const content = article.content || '';
                      const combinedText = description ? `${description}\n\n${content}` : content;
                      const previewText = combinedText ? (combinedText.length > 100 ? combinedText.substring(0, 100) + '...' : combinedText) : '(No description)';
                      const isSelected = article.url && selectedArticleUrls.has(article.url);
                      
                      return (
                        <tr key={`${article.recordId}-${index}`}>
                          <td>
                            {article.url ? (
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleArticleSelect(article.url)}
                              />
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                          <td>
                            {article.url ? (
                              <button
                                type="button"
                                className="btn btn-link p-0 text-start text-decoration-none"
                                style={{ cursor: 'pointer', wordBreak: 'break-word' }}
                                onClick={() => setSelectedArticleForDescription(article)}
                              >
                                {article.title || article.url}
                              </button>
                            ) : (
                              <span className="text-muted">{article.title || '(No title)'}</span>
                            )}
                          </td>
                          <td>
                            {article.taskInfo || '新聞'}
                          </td>
                          <td>{article.recordCreatedAt || '-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* Description Modal */}
      {selectedArticleForDescription && (
        <>
          {/* Modal Backdrop */}
          <div 
            className="modal-backdrop show" 
            onClick={() => setSelectedArticleForDescription(null)}
            style={{ zIndex: 1040 }}
          ></div>
          
          {/* Modal */}
          <div 
            className="modal show d-block" 
            tabIndex="-1" 
            role="dialog"
            style={{ zIndex: 1050 }}
          >
            <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    {selectedArticleForDescription.title || '文章描述'}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setSelectedArticleForDescription(null)}
                    aria-label="關閉"
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <strong>URL:</strong>
                    {selectedArticleForDescription.url ? (
                      <a 
                        href={selectedArticleForDescription.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="ms-2"
                      >
                        {selectedArticleForDescription.url}
                      </a>
                    ) : (
                      <span className="ms-2 text-muted">-</span>
                    )}
                  </div>
                  <div>
                    <strong>描述與內容：</strong>
                    <div 
                      className="mt-2 p-3 bg-light rounded"
                      style={{ 
                        maxHeight: '400px', 
                        overflowY: 'auto',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                      }}
                    >
                      {(() => {
                        const description = selectedArticleForDescription.description || '';
                        const content = selectedArticleForDescription.content || '';
                        const combinedText = description ? `${description}\n\n${content}` : content;
                        return combinedText || '(No description or content available)';
                      })()}
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  {selectedArticleForDescription.url && (
                    <a
                      href={selectedArticleForDescription.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-primary"
                    >
                      前往文章
                    </a>
                  )}
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setSelectedArticleForDescription(null)}
                  >
                    關閉
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Topic Input Modal */}
      {showTopicInputModal && (
        <>
          {/* Modal Backdrop */}
          <div 
            className="modal-backdrop show" 
            onClick={handleCancelTopicInput}
            style={{ zIndex: 1040 }}
          ></div>
          
          {/* Modal */}
          <div 
            className="modal show d-block" 
            tabIndex="-1" 
            role="dialog"
            style={{ zIndex: 1050 }}
          >
            <div className="modal-dialog modal-dialog-centered" role="document" onClick={(e) => e.stopPropagation()}>
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">輸入對齊主題（可選）</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleCancelTopicInput}
                    aria-label="關閉"
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">
                      對齊主題（可選）
                      <small className="text-muted ms-2">留空則不進行主題對齊</small>
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={topicTo}
                      onChange={(e) => setTopicTo(e.target.value)}
                      placeholder="例如：香港留學、移民政策、教育資訊等"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleGenerateArticles();
                        }
                      }}
                    />
                    <div className="form-text">
                      如果提供主題，AI 會將改寫後的文章與該主題自然對齊。可以留空直接繼續。
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCancelTopicInput}
                  >
                    取消
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleGenerateArticles}
                    disabled={generatingArticles}
                  >
                    {generatingArticles ? '生成中...' : '繼續生成'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NewsDataEditor;
